import { emptyCart } from "./cartStorage";

const itemSignature = (items) => items.map((item) => [item.product.id, item.qty, Object.entries(item.size_quantities || {}).sort(), item.details || ""]).sort((a, b) => a[0].localeCompare(b[0]));
const same = (a, b) => JSON.stringify(itemSignature(a)) === JSON.stringify(itemSignature(b));
const buckets = (item) => {
  if (!item) return {};
  const sizes = { ...(item.size_quantities || {}) };
  const assigned = Object.values(sizes).reduce((sum, qty) => sum + qty, 0);
  if (item.qty > assigned) sizes[""] = item.qty - assigned;
  return sizes;
};

// Apply only changes made locally since the last acknowledged snapshot, not a stale whole cart.
function rebase(base, local, remote, submitted) {
  const previous = new Map(base.map((item) => [item.product.id, item]));
  const desired = new Map(local.map((item) => [item.product.id, item]));
  const result = new Map(remote.map((item) => [item.product.id, item]));
  for (const id of new Set([...previous.keys(), ...desired.keys()])) {
    const item = desired.get(id);
    const old = previous.get(id);
    if (!item) { result.delete(id); continue; }
    if (old && same([old], [item])) continue;
    const other = result.get(id);
    const before = buckets(old);
    const after = buckets(item);
    const merged = buckets(other);
    for (const size of new Set([...Object.keys(before), ...Object.keys(after)])) {
      const delta = (after[size] || 0) - (before[size] || 0);
      if (!delta) continue;
      const target = submitted ? Math.max(0, delta) : Math.max(0, (merged[size] || 0) + delta);
      if (target) merged[size] = target;
      else delete merged[size];
    }
    const qty = Object.values(merged).reduce((sum, count) => sum + count, 0);
    if (!qty) { result.delete(id); continue; }
    const notesChanged = (item.details || "") !== (old?.details || "");
    result.set(id, {
      product: other?.product || item.product, qty,
      size_quantities: Object.fromEntries(Object.entries(merged).filter(([size]) => size !== "")),
      details: notesChanged || !other ? (item.details || "") : (other.details || ""),
    });
  }
  return [...result.values()];
}

export function reconcileCart(local, remote) {
  const submitted = remote?.state === "submitted";
  const remoteItems = remote && !submitted ? remote.items : [];
  const sameCart = remote?.cart_id === local.cartId;
  let dirty = local.dirty;
  let base = local.baseItems || [];
  if (sameCart && local.attempt && remote?.acknowledged_mutations?.includes(local.attempt.changeId)) {
    base = local.attempt.items;
    dirty = local.changeId !== local.attempt.changeId;
  }
  if (local.version !== 2) {
    dirty = local.items.length > 0 && (!remote || (sameCart && local.revision > remote.revision));
    base = sameCart ? remote.items : [];
  }
  if (sameCart && local.changeId && remote?.acknowledged_mutations?.includes(local.changeId)) dirty = false;
  // A submitted snapshot is the baseline: only extra, not-yet-submitted pieces can carry forward.
  if (submitted && sameCart) base = remote.items;
  const items = dirty ? rebase(base, local.items, remoteItems, submitted && sameCart) : remoteItems;
  const needsSave = !same(items, remoteItems);
  const usableRemote = remote && !submitted;
  const cartId = usableRemote ? remote.cart_id : (sameCart ? emptyCart().cartId : local.cartId);
  return {
    ...local, version: 2, cartId, items, baseItems: remoteItems,
    baseRevision: usableRemote ? remote.revision : 0,
    revision: needsSave ? Math.max(Date.now(), local.revision + 1, (remote?.revision || 0) + 1) : (usableRemote ? remote.revision : 0),
    dirty: needsSave, changeId: needsSave ? (local.changeId || crypto.randomUUID()) : null,
    attempt: null,
  };
}

export function acknowledgeCart(current, sent, result) {
  if (current.cartId !== sent.cartId) return current;
  const changedAgain = current.changeId !== sent.changeId;
  return {
    ...current, baseItems: sent.items, baseRevision: result.revision,
    revision: changedAgain ? Math.max(current.revision, result.revision + 1) : result.revision,
    dirty: changedAgain, changeId: changedAgain ? current.changeId : null,
    attempt: null,
  };
}